import {
  FPlusViewer,
  FPlusViewerWithOverflowIndicator,
} from '@festina-life/fplus'
import { type Ref, useMemo } from 'react'

import { useFormatFPlusExpression } from '../../uiModels/api/formatter/useFormatFPlusExpression'
import { functionBody } from '../templateFieldsTable/functionHelpers'
import type { ExternalFPlusOverflowViewerProps } from './types'

export const OVERFLOW_LINE_WIDTH = 200

type Props = {
  /**
   * Original code shown in the editor, without formatting.
   */
  code: string
  /**
   * Line width for which the code should be formatted.
   */
  width?: number
  expressionPath: string
  shouldShowOverflowFallback: boolean
  isShowingFunctionDeclaration: boolean
} & ExternalFPlusOverflowViewerProps

/**
 * Given an FPlus expression this component will mount a formated code viewer for the expression, such that the code
 * is formatted to a given line length.
 */
export const FormattedFPlusViewer = ({
  code,
  width,
  expressionPath,
  shouldShowOverflowFallback,
  isShowingFunctionDeclaration,
  alignment,
  className,
  ...other
}: Props) => {
  const { formattedCode, ref: containerRef } = useFormatFPlusExpression(code)

  const { formattedCode: tooltipCode } = useFormatFPlusExpression(
    code,
    OVERFLOW_LINE_WIDTH,
  )

  const viewingType = shouldShowOverflowFallback ? 'overflow' : 'simple'
  const nonOverflownCode = formattedCode ?? code
  const viewerCode = useMemo(
    () =>
      isShowingFunctionDeclaration
        ? nonOverflownCode
        : functionBody(nonOverflownCode),
    [nonOverflownCode, isShowingFunctionDeclaration],
  )
  const overflownCode = tooltipCode ?? code
  const overflownViewerCode = useMemo(
    () =>
      isShowingFunctionDeclaration
        ? overflownCode
        : functionBody(overflownCode),
    [isShowingFunctionDeclaration, overflownCode],
  )

  return (
    <div className="size-full" ref={containerRef as Ref<HTMLDivElement>}>
      {code &&
        (viewingType === 'simple' ? (
          <FPlusViewer
            code={viewerCode}
            alignment={alignment}
            className={className}
          />
        ) : (
          <FPlusViewerWithOverflowIndicator
            code={viewerCode}
            tooltipCode={overflownViewerCode}
            alignment={alignment}
            className={className}
            {...other}
          />
        ))}
    </div>
  )
}
